/**
 * #MT-14DB846 — Doca Expedição (Movimentações).
 * Tela no padrão CRUD SIAL (grade + filtro + operações), como Saídas / Caixas Picking.
 */
import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import pdf from '../../versa/pdf.js';
import { notify, ask } from '../../versa/msgbox.js';
import view from '../views/dock-expedition.js';
import form from '../forms/dock-expedition.js';
import romaneioForm from '../forms/dock-expedition-romaneio.js';
import * as types from '../types.js';
import {
	resolveCompanyId,
	resolveWarehouseId,
	resolveLocationIdForMove,
} from '../utils/location-search.js';

function isCountQuery(query)
{
	const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
	return qs.get('group') === 'count';
}

function pageBounds(query)
{
	const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
	const limit = Number(qs.get('limit') || 30);
	const offset = Number(qs.get('offset') || 0);
	return {
		limit: Number.isFinite(limit) ? limit : 30,
		offset: Number.isFinite(offset) ? offset : 0,
	};
}

function filtersToParams(filterData)
{
	const params = {};
	for (let i = 0; i < 12; i++)
	{
		const field = filterData?.[`field-${i}`];
		const value = filterData?.[`value-${i}`];
		if (!field || value == null || value === '')
			continue;
		if (['company_id', 'carrier_id', 'reference', 'order_id', 'id', 'output_id'].includes(field))
			params[field === 'output_id' ? 'id' : field] = value;
	}
	return params;
}

function normalizeRow(row)
{
	const id = Number(row.output_id ?? row.id);
	return {
		...row,
		id,
		output_id: id,
		status: Number(row.status) || 75,
	};
}

function hasDock(row)
{
	return Boolean(row?.dock_address || (row?.dock_warehouse_id && row?.dock_location_id));
}

const dockExpedition = function ()
{
	const dock_crud = crud.create({

		_lastSearchKey: null,
		_lastSearchRows: null,

		insertRecord()
		{
			notify(
				'Esta tela lista saídas já em Doca Expedição. Use Filtrar / Operações para vincular posição ou avançar.',
				'Doca Expedição',
			);
		},

		async loadBaseData()
		{
			const q = this.queryAll();
			const [companies, warehouses, carriers, customers] = await Promise.all([
				api.call(api.list_companies, q),
				api.call(api.list_warehouses, q),
				api.call(api.list_carriers, q),
				api.call(api.list_customers, q),
			]);
			await Promise.all([
				db.set('companies', companies || []).catch(() => {}),
				db.set('warehouses', warehouses || []).catch(() => {}),
				db.set('carriers', carriers || []).catch(() => {}),
				db.set('customers', customers || []).catch(() => {}),
			]);
		},

		async queryData(query)
		{
			const filterParams = filtersToParams(this.filter || {});
			let companies = filterParams.company_id
				? [{ id: filterParams.company_id }]
				: await db.list('companies').catch(() => []);
			if (!companies?.length)
				companies = await api.call(api.list_companies, '?limit=0') || [];
			// Evita varrer tenants com param OFF (http.js mostra modal «Atenção» em cada 400).
			// Cache IndexedDB antigo sem o campo: ainda consulta (API lista devolve []).
			companies = (companies || []).filter((c) =>
			{
				if (!c)
					return false;
				const v = c.out_dock_expedition_enabled;
				if (v === false || v === 0 || v === '0' || v === 'false')
					return false;
				return true;
			});
			if (!companies.length)
				return isCountQuery(query) ? 0 : [];

			const searchKey = JSON.stringify({
				companies: companies.map((c) => c.id).sort(),
				carrier_id: filterParams.carrier_id || '',
				reference: filterParams.reference || '',
				order_id: filterParams.order_id || '',
				id: filterParams.id || '',
			});

			let rows = this._lastSearchKey === searchKey && Array.isArray(this._lastSearchRows)
				? this._lastSearchRows
				: null;

			if (!rows)
			{
				const lists = await Promise.all(companies.map(async (company) =>
				{
					const q = new URLSearchParams();
					q.set('limit', '500');
					if (filterParams.carrier_id)
						q.set('carrier_id', String(filterParams.carrier_id));
					if (filterParams.reference)
						q.set('reference', String(filterParams.reference));
					try
					{
						const data = await api.call(api.list_dock_expedition, company.id, `?${q.toString()}`);
						return (Array.isArray(data) ? data : []).map(normalizeRow);
					}
					catch (e)
					{
						console.warn('dock-expedition list', company.id, e);
						return [];
					}
				}));
				rows = lists.flat();
				if (filterParams.order_id)
					rows = rows.filter((r) => Number(r.order_id) === Number(filterParams.order_id));
				if (filterParams.id)
					rows = rows.filter((r) => Number(r.id) === Number(filterParams.id));
				this._lastSearchKey = searchKey;
				this._lastSearchRows = rows;
			}

			if (isCountQuery(query))
				return rows.length;
			const { limit, offset } = pageBounds(query);
			return rows.slice(offset, offset + limit);
		},

		async reloadPage()
		{
			this._lastSearchKey = null;
			this._lastSearchRows = null;
			return Object.getPrototypeOf(this).reloadPage.call(this);
		},

		async loadRecord(id)
		{
			const [company_id, output_id] = JSON.parse(id);
			const cached = (this._lastSearchRows || []).find((r) =>
				String(r.company_id) === String(company_id) && Number(r.id) === Number(output_id)
			);
			if (cached)
			{
				return {
					company_id: cached.company_id,
					output_id: cached.id,
					order_id: cached.order_id,
					reference: cached.reference || '',
					dock_warehouse_id: cached.dock_warehouse_id || '',
					dock_location_id: cached.dock_location_id || '',
					_dock_address: cached.dock_address || '',
				};
			}
			const row = await api.call(api.select_dock_expedition, company_id, output_id);
			const n = normalizeRow(row);
			return {
				company_id: n.company_id,
				output_id: n.id,
				order_id: n.order_id,
				reference: n.reference || '',
				dock_warehouse_id: n.dock_warehouse_id || '',
				dock_location_id: n.dock_location_id || '',
				_dock_address: n.dock_address || '',
			};
		},

		async saveRecord(data)
		{
			const co = await resolveCompanyId(data.company_id) || data.company_id;
			const wh = await resolveWarehouseId(co, data.dock_warehouse_id);
			let loc = Number(data.dock_location_id);
			if (!Number.isInteger(loc) || loc < 1)
				loc = await resolveLocationIdForMove(co, wh, data.dock_location_id, { isExpedition: true });
			if (!co)
				throw new Error('Selecione a empresa na lista (nick).');
			if (!Number.isInteger(wh) || wh < 1)
				throw new Error('Selecione o armazém na lista (nome).');
			if (!Number.isInteger(loc) || loc < 1)
				throw new Error('Posição inválida. Use um endereço de Expedição da lista (case-sensitive).');
			await api.call(api.patch_dock_expedition, co, data.output_id, {
				dock_warehouse_id: wh,
				dock_location_id: loc,
			});
			this._lastSearchKey = null;
			this._lastSearchRows = null;
			return {};
		},

		editRecord(data)
		{
			const initial = { ...data };
			const addr = data._dock_address;
			delete initial._dock_address;
			const fs = formset.create(form, async (formData) =>
			{
				try
				{
					await this.saveRecord(formData);
					notify('Posição de Expedição vinculada.', 'Sucesso');
					fs.destroy();
					await this.reloadPage();
				}
				catch (e)
				{
					notify(e?.message || String(e), 'Doca Expedição');
				}
			});
			const origInit = fs.init.bind(fs);
			fs.init = function ()
			{
				origInit();
				fs.form.setValues(initial);
				fs.form.refresh();
				if (addr)
				{
					const locNode = fs.dialog.getNode().querySelector('#dock_location_id');
					if (locNode)
						locNode.value = addr;
				}
			};
			fs.init();
		},

		async dockLink()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (ids.length !== 1)
			{
				notify('Selecione exatamente uma saída para vincular a posição.', 'Doca Expedição');
				return;
			}
			const data = await this.loadRecord(JSON.stringify(ids[0]));
			this.editRecord(data);
		},

		async dockAdvance()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (!ids.length)
			{
				notify('Selecione ao menos uma saída.', 'Doca Expedição');
				return;
			}
			const byCompany = new Map();
			for (const [company_id, output_id] of ids)
			{
				const row = (this._lastSearchRows || []).find((r) =>
					String(r.company_id) === String(company_id) && Number(r.id) === Number(output_id)
				);
				if (!hasDock(row))
				{
					notify(
						`Saída ${output_id} sem posição de Expedição. Vincule antes de avançar.`,
						'Atenção',
					);
					return;
				}
				if (!byCompany.has(company_id))
					byCompany.set(company_id, []);
				byCompany.get(company_id).push(Number(output_id));
			}
			ask(
				`Confirma avançar ${ids.length} saída(s)? Será feita a baixa de estoque (R→O) e a conclusão.`,
				'Avançar Doca Expedição',
				(key) =>
				{
					if (key !== 'Y')
						return;
					void (async () =>
					{
						try
						{
							let total = 0;
							let errN = 0;
							for (const [company_id, output_ids] of byCompany)
							{
								const resp = await api.call(api.advance_dock_expedition, company_id, {
									output_ids,
								});
								total += resp?.count ?? 0;
								errN += (resp?.errors || []).length;
								if (resp?.errors?.length)
									console.warn('dock-expedition advance', resp.errors);
							}
							notify(
								`Avançadas: ${total}.${errN ? ` Falhas: ${errN}.` : ''}`,
								errN ? 'Atenção' : 'Sucesso',
							);
							await this.reloadPage();
						}
						catch (e)
						{
							notify(e?.message || String(e), 'Doca Expedição');
						}
					})();
				},
			);
		},

		async _selectedByCompany()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (!ids.length)
			{
				notify('Selecione ao menos uma saída.', 'Doca Expedição');
				return null;
			}
			const byCompany = new Map();
			for (const [company_id, output_id] of ids)
			{
				if (!byCompany.has(company_id))
					byCompany.set(company_id, []);
				byCompany.get(company_id).push(Number(output_id));
			}
			return byCompany;
		},

		printRomaneio()
		{
			void (async () =>
			{
				const byCompany = await this._selectedByCompany();
				if (!byCompany)
					return;
				if (byCompany.size > 1)
				{
					notify('Selecione saídas de uma única empresa por vez para o romaneio.', 'Romaneio');
					return;
				}
				const [company_id, output_ids] = [...byCompany.entries()][0];
				const fs = formset.create(romaneioForm, async (data) =>
				{
					try
					{
						const resp = await api.call(api.print_dock_romaneio, company_id, {
							output_ids,
							driver_name: data.driver_name || '',
							vehicle_plate: data.vehicle_plate || '',
							driver_doc: data.driver_doc || '',
							operation_nature: data.operation_nature || 'VENDA',
							departure_date: data.departure_date || '',
							departure_time: data.departure_time || '',
							notes: data.notes || '',
						});
						if (resp?.pdf)
						{
							pdf(resp.pdf);
							fs.destroy();
						}
						else
							notify('Nenhum PDF retornado.', 'Romaneio');
					}
					catch (e)
					{
						notify(e?.message || String(e), 'Romaneio');
					}
				});
				fs.init();
			})();
		},

		printAvisoRemessa()
		{
			void (async () =>
			{
				const byCompany = await this._selectedByCompany();
				if (!byCompany)
					return;
				const n = [...byCompany.values()].reduce((a, b) => a + b.length, 0);
				ask(
					`Incluir ${n} saída(s) no Aviso de Remessa?`,
					'Aviso de Remessa',
					(answer) =>
					{
						if (answer !== 'Y')
							return;
						void (async () =>
						{
							try
							{
								for (const [company_id, output_ids] of byCompany)
								{
									const resp = await api.call(api.print_dock_aviso_remessa, company_id, {
										output_ids,
									});
									if (resp?.pdf)
										pdf(resp.pdf);
									else
										notify('Nenhum PDF retornado para a empresa selecionada.', 'Aviso de Remessa');
								}
							}
							catch (e)
							{
								notify(e?.message || String(e), 'Aviso de Remessa');
							}
						})();
					},
				);
			})();
		},
	});

	const fieldspecs = [
		{ index: 0, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 1, field: 'id', label: 'Saída', types: [types.number(0, 2)], placeholder: '0', title: 'Número da saída' },
		{ index: 2, field: 'order_id', label: 'Pedido', types: [types.number(0, 2)], placeholder: '0', title: 'Número do pedido' },
		{ index: 3, field: 'reference', label: 'Referência', types: [types.name()], placeholder: '', title: 'Referência do pedido' },
		{ index: 4, field: 'customer_id', label: 'Cliente', types: [types.datalist()], placeholder: '', title: 'Cliente', store: 'customers', labels: ['nick'] },
		{ index: 5, field: 'carrier_id', label: 'Transportadora', types: [types.datalist()], placeholder: '', title: 'Transportadora', store: 'carriers', labels: ['nick'] },
		{ index: 6, field: 'dock_warehouse_id', label: 'Armazém Expedição', types: [types.datalist()], placeholder: '', title: 'Armazém da doca', store: 'warehouses', labels: ['name'] },
		{ index: 7, field: 'dock_location_id', label: 'Posição Expedição', types: [types.name()], placeholder: '', title: 'Endereço da posição de Expedição' },
		{ index: 8, field: 'status', label: 'Status', types: [types.opt_output_status(), types.datalist()], placeholder: '', title: 'Status da saída' },
		{ index: 9, field: 'created_on', label: 'Criação', types: [types.datetime()], placeholder: '', title: 'Data/hora de criação' },
	];

	const operations = [
		{ hash: '#dock-link', label: 'Vincular&nbsp;posição' },
		{ hash: '#dock-advance', label: 'Avançar&nbsp;selecionadas' },
	];

	const reports = [
		{ hash: '#dock-print-romaneio', label: 'Romaneio&nbsp;de&nbsp;Transportadora' },
		{ hash: '#dock-print-ar', label: 'Aviso&nbsp;de&nbsp;Remessa' },
	];

	router.set('#dock-link', () => dock_crud.dockLink());
	router.set('#dock-advance', () => dock_crud.dockAdvance());
	router.set('#dock-print-romaneio', () => dock_crud.printRomaneio());
	router.set('#dock-print-ar', () => dock_crud.printAvisoRemessa());

	dock_crud.run(
		'dock-expedition',
		'Doca Expedição',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports,
		{
			default_columns: '0,1,2,3,4,5,6,7,8,9',
		},
	);
};

export default dockExpedition;
